import { useState, useEffect, useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Search, Ship, Plane, Truck, MapPin, Clock, CheckCircle, 
  AlertCircle, Package, RefreshCw, Calendar, ArrowRight,
  Globe, FileText, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SHIPMENT_STATUSES = [
  { value: 'confirmed', label: 'Confirmed', label_es: 'Confirmado', color: 'bg-blue-500' },
  { value: 'picked-up', label: 'Picked Up', label_es: 'Recogido', color: 'bg-indigo-500' },
  { value: 'in-transit', label: 'In Transit', label_es: 'En Tránsito', color: 'bg-purple-500' },
  { value: 'customs', label: 'In Customs', label_es: 'En Aduanas', color: 'bg-orange-500' },
  { value: 'customs-cleared', label: 'Customs Cleared', label_es: 'Despacho Aduanero', color: 'bg-teal-500' },
  { value: 'out-for-delivery', label: 'Out for Delivery', label_es: 'En Camino de Entrega', color: 'bg-cyan-500' },
  { value: 'delivered', label: 'Delivered', label_es: 'Entregado', color: 'bg-green-500' },
  { value: 'cancelled', label: 'Cancelled', label_es: 'Cancelado', color: 'bg-red-500' }
];

const TrackingDashboardPage = () => {
  const { i18n } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const isSpanish = i18n.language === 'es';
  const isEmployee = user?.role === 'admin' || user?.role === 'employee';
  
  const [trackingNumber, setTrackingNumber] = useState(searchParams.get('tracking') || '');
  const [loading, setLoading] = useState(false);
  const [shipmentData, setShipmentData] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  
  // Add event dialog
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    status: '',
    location: '',
    description: '',
    description_es: ''
  });

  const searchShipment = useCallback(async () => {
    if (!trackingNumber.trim()) {
      toast.error(isSpanish ? 'Ingrese un número de tracking' : 'Enter a tracking number');
      return;
    }
    
    setLoading(true);
    setError('');
    setShipmentData(null);
    setEvents([]);
    
    try {
      const res = await fetch(`${API_URL}/api/tracking/${trackingNumber}/timeline`, {
        credentials: 'include'  // Send httpOnly cookies
      });
      
      if (res.ok) {
        const data = await res.json();
        setShipmentData(data.shipment);
        setEvents(data.events || []);
      } else if (res.status === 404) {
        setError(isSpanish ? 'Envío no encontrado' : 'Shipment not found');
      } else {
        setError(isSpanish ? 'Error al buscar' : 'Search failed');
      }
    } catch (err) {
      setError(isSpanish ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  }, [trackingNumber, isSpanish]);

  useEffect(() => {
    if (trackingNumber) {
      searchShipment();
    }
  }, [trackingNumber, searchShipment]);

  const handleAddEvent = async () => {
    if (!newEvent.status || !newEvent.location || !newEvent.description) {
      toast.error(isSpanish ? 'Complete los campos requeridos' : 'Fill required fields');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/employee/shipments/${trackingNumber}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newEvent)
      });
      
      if (res.ok) {
        toast.success(isSpanish ? 'Evento añadido' : 'Event added successfully');
        setIsAddEventOpen(false);
        setNewEvent({ status: '', location: '', description: '', description_es: '' });
        searchShipment(); // Refresh
      } else {
        toast.error(isSpanish ? 'Error al añadir evento' : 'Failed to add event');
      }
    } catch (err) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    }
  };

  const getStatusInfo = (status) => {
    return SHIPMENT_STATUSES.find(s => s.value === status) || SHIPMENT_STATUSES[0];
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'air': return Plane;
      case 'ocean': return Ship;
      case 'road': return Truck;
      default: return Package;
    }
  };

  // Auth check for protected features
  if (authLoading) {
    return (
      <div className="min-h-screen bg-steel-50 pt-24 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-steel-50 pt-24" data-testid="tracking-dashboard-page">
      {/* Header */}
      <section className="bg-navy-900 py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="text-gold text-sm font-bold tracking-widest uppercase mb-4 block">
              {isSpanish ? 'Seguimiento en Tiempo Real' : 'Real-Time Tracking'}
            </span>
            <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
              {isSpanish ? 'Rastrear su Envío' : 'Track Your Shipment'}
            </h1>
            <p className="text-steel-300 mb-8">
              {isSpanish 
                ? 'Ingrese su número de tracking para ver el estado detallado de su envío'
                : 'Enter your tracking number to see detailed shipment status'}
            </p>
            
            {/* Search Box */}
            <div className="flex gap-3 max-w-lg mx-auto">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                <Input
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && searchShipment()}
                  placeholder={isSpanish ? 'Ej: EC-20260315-ABC123' : 'e.g., EC-20260315-ABC123'}
                  className="pl-12 h-14 text-lg bg-white"
                  data-testid="tracking-input"
                />
              </div>
              <Button 
                onClick={searchShipment}
                className="btn-accent h-14 px-8"
                disabled={loading}
                data-testid="track-button"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  isSpanish ? 'Rastrear' : 'Track'
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12">
        <div className="container-custom max-w-4xl">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-lg p-6 text-center"
            >
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-700 font-medium">{error}</p>
              <p className="text-red-600 text-sm mt-2">
                {isSpanish 
                  ? 'Verifique el número de tracking e intente de nuevo'
                  : 'Please verify the tracking number and try again'}
              </p>
            </motion.div>
          )}

          {shipmentData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Shipment Overview Card */}
              <Card className="border-0 shadow-xl overflow-hidden">
                <div className={`${getStatusInfo(shipmentData.status).color} px-6 py-4`}>
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const ModeIcon = getModeIcon(shipmentData.transport_mode);
                        return <ModeIcon className="w-6 h-6" />;
                      })()}
                      <div>
                        <p className="text-sm opacity-80">{isSpanish ? 'Número de Tracking' : 'Tracking Number'}</p>
                        <p className="text-xl font-bold">{shipmentData.tracking_number}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm opacity-80">{isSpanish ? 'Estado' : 'Status'}</p>
                      <p className="text-xl font-bold">
                        {isSpanish 
                          ? getStatusInfo(shipmentData.status).label_es 
                          : getStatusInfo(shipmentData.status).label}
                      </p>
                    </div>
                  </div>
                </div>
                
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Route Info */}
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-gold" />
                        </div>
                        <div className="w-0.5 h-8 bg-steel-200" />
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-green-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="mb-4">
                          <p className="text-sm text-steel-500">{isSpanish ? 'Origen' : 'Origin'}</p>
                          <p className="font-semibold text-navy-800">{shipmentData.origin}</p>
                        </div>
                        <div>
                          <p className="text-sm text-steel-500">{isSpanish ? 'Destino' : 'Destination'}</p>
                          <p className="font-semibold text-navy-800">{shipmentData.destination}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Details */}
                    <div className="space-y-3">
                      {shipmentData.current_location && (
                        <div className="flex items-center gap-3">
                          <Globe className="w-5 h-5 text-steel-400" />
                          <div>
                            <p className="text-sm text-steel-500">{isSpanish ? 'Ubicación Actual' : 'Current Location'}</p>
                            <p className="font-medium text-navy-800">{shipmentData.current_location}</p>
                          </div>
                        </div>
                      )}
                      {shipmentData.estimated_delivery && (
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-steel-400" />
                          <div>
                            <p className="text-sm text-steel-500">{isSpanish ? 'Entrega Estimada' : 'Estimated Delivery'}</p>
                            <p className="font-medium text-navy-800">
                              {new Date(shipmentData.estimated_delivery).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      )}
                      {shipmentData.client_name && (
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-steel-400" />
                          <div>
                            <p className="text-sm text-steel-500">{isSpanish ? 'Cliente' : 'Client'}</p>
                            <p className="font-medium text-navy-800">{shipmentData.client_name}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Add Event Button for Employees */}
                  {isEmployee && (
                    <div className="mt-6 pt-6 border-t border-steel-200">
                      <Button 
                        onClick={() => setIsAddEventOpen(true)}
                        className="btn-accent"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {isSpanish ? 'Añadir Evento de Tracking' : 'Add Tracking Event'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gold" />
                    {isSpanish ? 'Historial de Eventos' : 'Event History'}
                    <span className="ml-auto text-sm font-normal text-steel-500">
                      {events.length} {isSpanish ? 'eventos' : 'events'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {events.length === 0 ? (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-steel-300 mx-auto mb-4" />
                      <p className="text-steel-500">
                        {isSpanish ? 'No hay eventos registrados aún' : 'No events recorded yet'}
                      </p>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-steel-200" />
                      
                      {/* Events */}
                      <div className="space-y-6">
                        {events.map((event, index) => {
                          const statusInfo = getStatusInfo(event.status);
                          const isLatest = index === events.length - 1;
                          return (
                            <motion.div
                              key={event.id || index}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="relative pl-16"
                            >
                              {/* Status dot */}
                              <div className={`absolute left-4 w-5 h-5 rounded-full ${statusInfo.color} ${isLatest ? 'ring-4 ring-opacity-30 ring-current' : ''} flex items-center justify-center`}>
                                {isLatest && <div className="w-2 h-2 bg-white rounded-full" />}
                              </div>
                              
                              {/* Event content */}
                              <div className={`bg-steel-50 rounded-lg p-4 ${isLatest ? 'ring-2 ring-gold/30' : ''}`}>
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium text-white ${statusInfo.color}`}>
                                      {isSpanish ? statusInfo.label_es : statusInfo.label}
                                    </span>
                                    <p className="mt-2 font-medium text-navy-800">
                                      {isSpanish ? (event.description_es || event.description) : event.description}
                                    </p>
                                    {event.location && (
                                      <p className="text-sm text-steel-500 mt-1 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" /> {event.location}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right text-sm text-steel-500 whitespace-nowrap">
                                    {event.timestamp && (
                                      <>
                                        <p>{new Date(event.timestamp).toLocaleDateString()}</p>
                                        <p>{new Date(event.timestamp).toLocaleTimeString()}</p>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Initial state - no search yet */}
          {!shipmentData && !error && !loading && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-steel-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-navy-800 mb-2">
                {isSpanish ? 'Ingrese su número de tracking' : 'Enter Your Tracking Number'}
              </h3>
              <p className="text-steel-500">
                {isSpanish 
                  ? 'Podrá ver el estado completo y el historial de su envío'
                  : "You'll see the complete status and history of your shipment"}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Add Event Dialog */}
      <Dialog open={isAddEventOpen} onOpenChange={setIsAddEventOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isSpanish ? 'Añadir Evento de Tracking' : 'Add Tracking Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{isSpanish ? 'Estado' : 'Status'} *</Label>
              <Select value={newEvent.status} onValueChange={(v) => setNewEvent({...newEvent, status: v})}>
                <SelectTrigger><SelectValue placeholder={isSpanish ? 'Seleccionar' : 'Select'} /></SelectTrigger>
                <SelectContent>
                  {SHIPMENT_STATUSES.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      {isSpanish ? status.label_es : status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{isSpanish ? 'Ubicación' : 'Location'} *</Label>
              <Input 
                value={newEvent.location}
                onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                placeholder={isSpanish ? 'Ej: Rotterdam, Netherlands' : 'e.g., Rotterdam, Netherlands'}
              />
            </div>
            <div>
              <Label>{isSpanish ? 'Descripción (EN)' : 'Description (EN)'} *</Label>
              <Textarea 
                value={newEvent.description}
                onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                placeholder={isSpanish ? 'Descripción del evento...' : 'Event description...'}
                rows={2}
              />
            </div>
            <div>
              <Label>{isSpanish ? 'Descripción (ES)' : 'Description (ES)'}</Label>
              <Textarea 
                value={newEvent.description_es}
                onChange={(e) => setNewEvent({...newEvent, description_es: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddEventOpen(false)}>
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleAddEvent} className="btn-accent">
              <Plus className="w-4 h-4 mr-2" />
              {isSpanish ? 'Añadir Evento' : 'Add Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrackingDashboardPage;
